import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * RouteTransition — an emerald curtain that wipes over the viewport on route
 * change, so navigation never reads as "click → white flash → new page".
 *
 * Why an overlay and not a page transform:
 *   Animating <main> (scale/translate) creates a containing block and
 *   SILENTLY BREAKS every `position: sticky` and pinned ScrollTrigger inside
 *   it — that bug cost this project a lot of debugging already. A separate
 *   fixed overlay leaves page layout completely untouched.
 *
 * Sequence on navigation:
 *   1. cover   — curtain scales up from the bottom (520ms)
 *   2. (React has already swapped the route; PageShell scrolls to top)
 *   3. reveal  — curtain collapses to the top (620ms), same direction, so it
 *                reads as one continuous wipe rather than a bounce.
 *
 * The curtain is pointer-events:none throughout, so it can never swallow a
 * click even mid-animation. Disabled entirely under reduced motion.
 */
export default function RouteTransition() {
  const location = useLocation();
  const [phase, setPhase] = useState("idle"); // idle | covering | revealing
  const firstRender = useRef(true);
  const timers = useRef([]);

  useEffect(() => {
    // Don't play a transition on the very first paint — the loading
    // sequence/hero owns that moment.
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    // Clear any in-flight timers if the user navigates again mid-transition.
    timers.current.forEach(clearTimeout);
    timers.current = [];

    setPhase("covering");
    timers.current.push(
      setTimeout(() => setPhase("revealing"), 520),
      setTimeout(() => setPhase("idle"), 520 + 620)
    );

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [location.pathname]);

  if (phase === "idle") return null;

  return (
    <div
      className={`route-curtain ${phase === "covering" ? "is-covering" : "is-revealing"}`}
      aria-hidden="true"
    >
      <span className="route-curtain__mark">Nausiddhi</span>
    </div>
  );
}
